import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mic, Square, CheckCircle, AlertCircle, X, Plus, Trash2, Info } from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

const CATEGORIES = ['incident', 'near_miss', 'hazard', 'complaint', 'audit_finding'] as const
const HRW_ACTIVITIES = [
  'Working at heights', 'Confined space entry', 'Electrical work', 'Demolition',
  'Asbestos work', 'Concrete pumping', 'Crane operation', 'Forklift operation',
  'Explosive blasting', 'Diving', 'Trenching/excavation',
]
const BODY_PARTS = [
  'Head', 'Eye', 'Neck', 'Shoulder', 'Arm', 'Hand / Finger', 'Back', 'Chest',
  'Abdomen', 'Hip', 'Leg', 'Knee', 'Foot / Toe', 'Internal', 'Multiple', 'Other',
]
const INJURY_NATURES = [
  'Laceration', 'Fracture', 'Sprain / Strain', 'Burn', 'Bruise / Contusion',
  'Crush injury', 'Puncture', 'Amputation', 'Electric shock', 'Concussion',
  'Chemical exposure', 'Other',
]
const MECHANISMS = [
  'Fall from height', 'Slip / trip / fall (same level)', 'Struck by object',
  'Caught in / between', 'Manual handling', 'Electrical contact',
  'Vehicle incident', 'Exposure to substance', 'Other',
]
const RELATIONSHIPS = ['Worker', 'Contractor', 'Visitor', 'Volunteer', 'Member of public']

type VoiceState = 'idle' | 'listening' | 'analysing' | 'done'
type PersonMode = 'me' | 'search' | 'visitor' | null

interface Witness { name: string; contact: string; statement: string }

const blankWitness = (): Witness => ({ name: '', contact: '', statement: '' })

export default function IncidentNew() {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [form, setForm] = useState({
    category: 'incident' as typeof CATEGORIES[number],
    description: '',
    date_occurred: new Date().toISOString().slice(0, 16),
    location_detail: '',
    activity_at_time: '',
    hrw_involved: [] as string[],
    // Injury outcome
    injury_type: '',
    injury_body_part: '',
    injury_nature: '',
    injury_mechanism: '',
    days_lost: 0,
    medical_treatment: false,
    lost_time: false,
    restricted_work: false,
    first_aid_only: false,
    property_damage: false,
    psychosocial_incident: false,
    violence_incident: false,
    // Person involved
    person_name: '',
    person_relationship: '',
    person_dob: '',
    person_address: '',
    person_contact: '',
    // Treatment & hospital
    treatment_received: '',
    taken_to_hospital: false,
    hospital_name: '',
    treating_practitioner: '',
    // Immediate site actions
    first_aid_administered: false,
    area_secured: false,
    immediate_actions: '',
    supervisor_notified: false,
    // WHS Act s35/s36 notifiable classification
    fatality: false,
    hospital_inpatient_immediate: false,
    specified_serious_injuries: [] as string[],
    dangerous_incident: false,
  })

  const [personMode, setPersonMode] = useState<PersonMode>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searching, setSearching] = useState(false)
  const [witnesses, setWitnesses] = useState<Witness[]>([])

  const [voiceState, setVoiceState] = useState<VoiceState>('idle')
  const [voiceVisible, setVoiceVisible] = useState(true)
  const [transcript, setTranscript] = useState('')
  const [voiceError, setVoiceError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [loading, setLoading] = useState(false)

  const recognitionRef = useRef<any>(null)
  const finalRef = useRef('')
  const mountedRef = useRef(true)
  const searchTimer = useRef<any>(null)

  const hasSpeechAPI = typeof window !== 'undefined' &&
    !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)

  useEffect(() => { return () => { mountedRef.current = false; try { recognitionRef.current?.abort() } catch {} } }, [])

  const hasInjury = form.medical_treatment || form.lost_time || form.restricted_work || form.first_aid_only
  const isInjuryEvent = form.category === 'incident' || hasInjury
  const showNotifiableSection = form.category === 'incident' || hasInjury

  // Real-time notifiable determination — matches server classifyNotifiable()
  const isActuallyNotifiable =
    form.fatality ||
    form.hospital_inpatient_immediate ||
    form.dangerous_incident ||
    form.specified_serious_injuries.some(s => s && s !== 'none')

  const toggleSSI = (val: string) => {
    if (val === 'none') {
      setForm(f => ({ ...f, specified_serious_injuries: ['none'] }))
    } else {
      setForm(f => ({
        ...f,
        specified_serious_injuries: f.specified_serious_injuries.includes(val)
          ? f.specified_serious_injuries.filter(x => x !== val)
          : [...f.specified_serious_injuries.filter(x => x !== 'none'), val],
      }))
    }
  }

  const SERIOUS_INJURIES = [
    { value: 'amputation',        label: 'Amputation' },
    { value: 'serious_head',      label: 'Serious head injury' },
    { value: 'serious_eye',       label: 'Serious eye injury' },
    { value: 'serious_burn',      label: 'Serious burn (requires treatment beyond first aid)' },
    { value: 'degloving',         label: 'Degloving or scalping injury' },
    { value: 'spinal',            label: 'Spinal injury' },
    { value: 'loss_of_function',  label: 'Loss of a bodily function' },
    { value: 'serious_laceration',label: 'Serious laceration (requires sutures or surgery)' },
    { value: 'loss_of_consciousness', label: 'Loss of consciousness (from head injury, electric shock, asphyxiation, or substance exposure)' },
  ]

  const DANGEROUS_INCIDENT_EXAMPLES = [
    'Uncontrolled escape, spill or leak of a substance',
    'Electrical short circuit, explosion or fire',
    'Fall or release of plant, substance or object from height',
    'Collapse, overturning or failure of plant (crane, EWP, forklift)',
    'Collapse or partial collapse of a structure or excavation',
    'Inrush of water, mud or gas into an excavation or tunnel',
    'Interruption to main ventilation in an underground mine',
  ]

  // Voice extraction — now maps additional fields
  const applyExtracted = (ex: any) => {
    setForm(f => ({
      ...f,
      description:      ex.description    || f.description,
      location_detail:  ex.location       || f.location_detail,
      activity_at_time: ex.activity       || f.activity_at_time,
      injury_body_part: ex.injury_body_part ? ex.injury_body_part : f.injury_body_part,
      injury_nature:    ex.injury_nature  ? mapNature(ex.injury_nature) : f.injury_nature,
      injury_mechanism: ex.injury_mechanism ? mapMechanism(ex.injury_mechanism) : f.injury_mechanism,
      immediate_actions: ex.immediate_actions || f.immediate_actions,
      area_secured:     ex.area_secured    ?? f.area_secured,
      supervisor_notified: ex.supervisor_notified ?? f.supervisor_notified,
      first_aid_administered: ex.first_aid_administered ?? f.first_aid_administered,
    }))
    // If AI says "person_is_self" → pre-select Me
    if (ex.person_is_self && personMode === null) {
      selectMe()
    } else if (ex.person_name && !ex.person_is_self && personMode === null) {
      setForm(f => ({ ...f, person_name: ex.person_name }))
      setPersonMode('visitor')
    }
  }

  function mapNature(raw: string): string {
    const map: Record<string, string> = {
      laceration: 'Laceration', fracture: 'Fracture',
      sprain_strain: 'Sprain / Strain', burn: 'Burn',
      bruise: 'Bruise / Contusion', crush: 'Crush injury',
      puncture: 'Puncture', amputation: 'Amputation',
      electric_shock: 'Electric shock', concussion: 'Concussion',
      chemical_exposure: 'Chemical exposure', other: 'Other',
    }
    return map[raw] || (INJURY_NATURES.includes(raw) ? raw : '')
  }
  function mapMechanism(raw: string): string {
    const map: Record<string, string> = {
      fall_from_height: 'Fall from height', slip_trip_fall: 'Slip / trip / fall (same level)',
      struck_by_object: 'Struck by object', caught_in_between: 'Caught in / between',
      manual_handling: 'Manual handling', electrical_contact: 'Electrical contact',
      vehicle_incident: 'Vehicle incident', exposure_to_substance: 'Exposure to substance',
      other: 'Other',
    }
    return map[raw] || (MECHANISMS.includes(raw) ? raw : '')
  }

  function selectMe() {
    setPersonMode('me')
    setForm(f => ({
      ...f,
      person_name: user ? `${user.firstName} ${user.lastName}` : '',
      person_relationship: 'Worker',
      // DOB/address may not be in authStore — leave blank for user to confirm
    }))
  }

  function selectVisitor() {
    setPersonMode('visitor')
    setForm(f => ({ ...f, person_name: '', person_relationship: 'Visitor', person_dob: '', person_address: '', person_contact: '' }))
  }

  function selectSearch() {
    setPersonMode('search')
    setForm(f => ({ ...f, person_name: '', person_relationship: '', person_dob: '', person_address: '', person_contact: '' }))
  }

  function pickWorkforce(member: any) {
    setForm(f => ({
      ...f,
      person_name:         `${member.first_name} ${member.last_name}`,
      person_relationship: member.is_contractor ? 'Contractor' : 'Worker',
      person_dob:          member.date_of_birth || '',
      person_address:      member.address || '',
      person_contact:      member.mobile || '',
    }))
    setSearchQuery(`${member.first_name} ${member.last_name}`)
    setSearchResults([])
  }

  useEffect(() => {
    if (personMode !== 'search' || searchQuery.trim().length < 2) { setSearchResults([]); return }
    clearTimeout(searchTimer.current)
    setSearching(true)
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/workforce?search=${encodeURIComponent(searchQuery)}&limit=8&with_user=1`)
        setSearchResults(data.data || data || [])
      } catch { setSearchResults([]) }
      setSearching(false)
    }, 300)
  }, [searchQuery, personMode])

  const stopAndAnalyse = () => { try { recognitionRef.current?.stop() } catch {} }
  const skip = () => { try { recognitionRef.current?.abort() } catch {}; setVoiceVisible(false); setVoiceState('idle'); setTranscript('') }

  const startListening = () => {
    if (!hasSpeechAPI) { setVoiceError("Speech recognition isn't available — use Chrome or Edge."); return }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const rec = new SR(); rec.continuous = true; rec.interimResults = true; rec.lang = 'en-AU'
    recognitionRef.current = rec; finalRef.current = ''; setTranscript(''); setVoiceError('')
    rec.onresult = (e: any) => {
      let interim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalRef.current += e.results[i][0].transcript + ' '
        else interim += e.results[i][0].transcript
      }
      setTranscript(finalRef.current + interim)
    }
    rec.onerror = (e: any) => {
      if (!mountedRef.current || e.error === 'aborted') return
      setVoiceState('idle')
      setVoiceError(e.error === 'not-allowed' ? 'Microphone access denied.' : e.error === 'network' ? 'Network error — type instead.' : `Error: ${e.error}`)
    }
    rec.onend = async () => {
      if (!mountedRef.current) return
      const text = finalRef.current.trim()
      if (!text) { setVoiceState('idle'); setVoiceError('Nothing captured — speak after Start, then tap Stop.'); return }
      setVoiceState('analysing')
      try {
        const { data } = await api.post('/incidents/voice-quick', { transcript: text })
        if (!mountedRef.current) return
        if (data.extracted) applyExtracted(data.extracted)
        setVoiceState('done')
      } catch {
        if (!mountedRef.current) return
        setVoiceState('idle'); setVoiceError('Analysis failed — fill in the form below.')
      }
    }
    rec.start(); setVoiceState('listening')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }))
  }
  const toggleHRW = (a: string) => setForm(f => ({ ...f, hrw_involved: f.hrw_involved.includes(a) ? f.hrw_involved.filter(x => x !== a) : [...f.hrw_involved, a] }))

  const addWitness = () => setWitnesses(w => [...w, blankWitness()])
  const removeWitness = (i: number) => setWitnesses(w => w.filter((_, j) => j !== i))
  const updateWitness = (i: number, field: keyof Witness, value: string) =>
    setWitnesses(w => w.map((x, j) => j === i ? { ...x, [field]: value } : x))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSubmitError(''); setLoading(true)
    try {
      const { data } = await api.post('/incidents', {
        ...form,
        days_lost: parseInt(String(form.days_lost)) || 0,
        witnesses: witnesses.filter(w => w.name.trim()),
      })
      navigate(`/incidents/${data.id}`)
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || 'Failed to create incident')
    }
    setLoading(false)
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Report Incident</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Captures all fields required for the WHS Register of Injuries and notifiable incident reporting</p>
      </div>

      {/* Voice panel */}
      {voiceVisible && (
        <div className="card space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              {voiceState === 'idle' && <button type="button" onClick={startListening} className="btn btn-primary flex items-center gap-2"><Mic size={16} />Start Voice</button>}
              {voiceState === 'listening' && <button type="button" onClick={stopAndAnalyse} className="btn btn-danger flex items-center gap-2"><Square size={14} fill="currentColor" />Stop</button>}
              {voiceState === 'analysing' && <button type="button" className="btn btn-secondary" disabled><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin inline-block mr-2" />Analysing…</button>}
              {voiceState === 'done' && <button type="button" onClick={() => { setVoiceState('idle'); setTranscript(''); finalRef.current = '' }} className="btn btn-secondary flex items-center gap-2"><CheckCircle size={16} />Re-record</button>}
              {voiceState === 'idle' && !voiceError && <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Speak — AI fills the form and pre-selects person involved</span>}
              {voiceState === 'listening' && <span className="text-sm flex items-center gap-1.5" style={{ color: 'var(--accent)' }}><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block" />Listening…</span>}
              {voiceState === 'done' && <span className="text-sm font-medium" style={{ color: 'var(--accent)' }}>✓ Form filled — review and complete below</span>}
            </div>
            <button type="button" onClick={skip} className="text-sm flex items-center gap-1 flex-shrink-0" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={14} />Skip</button>
          </div>
          {(voiceState === 'listening' || voiceState === 'analysing' || voiceState === 'done') && (
            <div className="rounded-lg p-3 text-sm min-h-[56px]" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}>
              {transcript ? <>{transcript}{voiceState === 'listening' && <span className="animate-pulse">▌</span>}</> : <span style={{ color: 'var(--text-muted)' }}>Speak now…</span>}
            </div>
          )}
          {voiceError && <div className="flex items-start gap-2 text-sm" style={{ color: 'var(--red)' }}><AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{voiceError}</div>}
        </div>
      )}
      {!voiceVisible && <button type="button" onClick={() => { setVoiceVisible(true); setVoiceState('idle'); setVoiceError('') }} className="text-sm flex items-center gap-1.5" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}><Mic size={13} />Use voice instead</button>}

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* ── 1. Incident Details ── */}
        <div className="card space-y-4">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Incident Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label>Category *</label>
              <select name="category" value={form.category} onChange={handleChange}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
              </select>
            </div>
            <div>
              <label>Date & Time *</label>
              <input type="datetime-local" name="date_occurred" value={form.date_occurred} onChange={handleChange} required />
            </div>
          </div>
          <div>
            <label>Description * <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>(What happened?)</span></label>
            <textarea name="description" value={form.description} onChange={handleChange} required rows={4} placeholder="Describe what happened in detail…" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label>Location</label><input name="location_detail" value={form.location_detail} onChange={handleChange} placeholder="e.g. Warehouse Zone B" /></div>
            <div><label>Activity at Time</label><input name="activity_at_time" value={form.activity_at_time} onChange={handleChange} placeholder="e.g. Manual handling, Operating forklift" /></div>
          </div>
        </div>

        {/* ── 2. High Risk Work ── */}
        <div className="card space-y-3">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>High Risk Work Involved</h2>
          <div className="flex flex-wrap gap-2">
            {HRW_ACTIVITIES.map(a => (
              <button key={a} type="button" onClick={() => toggleHRW(a)}
                className={`badge text-sm cursor-pointer border transition-all ${form.hrw_involved.includes(a) ? 'badge-high' : ''}`}
                style={!form.hrw_involved.includes(a) ? { background: 'var(--bg)', color: 'var(--text-secondary)', border: '1px solid var(--border)' } : {}}>
                {a}
              </button>
            ))}
          </div>
        </div>

        {/* ── 3. Injury & Outcome ── */}
        <div className="card space-y-4">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Injury & Outcome</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { name: 'medical_treatment', label: 'Medical Treatment Required' },
              { name: 'lost_time', label: 'Lost Time Injury' },
              { name: 'restricted_work', label: 'Restricted Work' },
              { name: 'first_aid_only', label: 'First Aid Only' },
              { name: 'property_damage', label: 'Property Damage' },
              { name: 'psychosocial_incident', label: 'Psychosocial Element' },
              { name: 'violence_incident', label: 'Violence / Aggression' },
            ].map(({ name, label }) => (
              <label key={name} className="flex items-center gap-2 cursor-pointer m-0 font-normal">
                <input type="checkbox" name={name} checked={(form as any)[name]} onChange={handleChange} className="w-4 h-4" style={{ width: '1rem' }} />
                <span className="text-sm" style={{ color: 'var(--text)' }}>{label}</span>
              </label>
            ))}
          </div>

          {/* Structured injury fields — shown when any injury outcome is selected */}
          {hasInjury && (
            <div className="space-y-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Injury Classification — required for Register of Injuries</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label>Body Part Affected</label>
                  <select name="injury_body_part" value={form.injury_body_part} onChange={handleChange}>
                    <option value="">— select —</option>
                    {BODY_PARTS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label>Nature of Injury</label>
                  <select name="injury_nature" value={form.injury_nature} onChange={handleChange}>
                    <option value="">— select —</option>
                    {INJURY_NATURES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label>Mechanism of Injury</label>
                  <select name="injury_mechanism" value={form.injury_mechanism} onChange={handleChange}>
                    <option value="">— select —</option>
                    {MECHANISMS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label>Days Lost</label>
                  <input type="number" name="days_lost" value={form.days_lost} onChange={handleChange} min={0} max={365} />
                </div>
              </div>
              <div>
                <label>Additional Injury Details <span className="font-normal text-xs" style={{ color: 'var(--text-muted)' }}>(optional free text)</span></label>
                <input name="injury_type" value={form.injury_type} onChange={handleChange} placeholder="e.g. Deep laceration requiring 4 sutures" />
              </div>
            </div>
          )}
        </div>

        {/* ── 4. WHS Act Notifiable Classification ── */}
        {showNotifiableSection && (
          <div className="card space-y-5">
            <div>
              <h2 className="font-semibold" style={{ color: 'var(--text)' }}>WHS Act Notifiable Classification</h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                Determines whether SafeWork notification is legally required under WHS Act s35/s36. Answer these questions — do not infer from injury type or days lost.
              </p>
            </div>

            {/* Q1: Fatality */}
            <div className="p-4 rounded-xl space-y-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>1. Did this incident result in a fatality?</p>
              <div className="flex gap-3">
                {[true, false].map(v => (
                  <button key={String(v)} type="button"
                    onClick={() => setForm(f => ({ ...f, fatality: v }))}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-all"
                    style={{
                      background: form.fatality === v ? (v ? '#FFEBE6' : 'var(--accent-light)') : 'var(--surface)',
                      color: form.fatality === v ? (v ? '#D93025' : 'var(--accent)') : 'var(--text-muted)',
                      border: form.fatality === v ? `2px solid ${v ? '#D93025' : 'var(--accent)'}` : '1px solid var(--border)',
                    }}>
                    {v ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
            </div>

            {/* Q2: Hospital inpatient */}
            <div className="p-4 rounded-xl space-y-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>2. Was the person admitted to hospital as an inpatient immediately following the incident?</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Treated in ED and released = No. Admitted overnight or longer = Yes.</p>
              <div className="flex gap-3">
                {[true, false].map(v => (
                  <button key={String(v)} type="button"
                    onClick={() => setForm(f => ({ ...f, hospital_inpatient_immediate: v }))}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-all"
                    style={{
                      background: form.hospital_inpatient_immediate === v ? (v ? '#FFEBE6' : 'var(--accent-light)') : 'var(--surface)',
                      color: form.hospital_inpatient_immediate === v ? (v ? '#D93025' : 'var(--accent)') : 'var(--text-muted)',
                      border: form.hospital_inpatient_immediate === v ? `2px solid ${v ? '#D93025' : 'var(--accent)'}` : '1px solid var(--border)',
                    }}>
                    {v ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
            </div>

            {/* Q3: Specified serious injuries */}
            <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>3. Does the injury involve any of the following specified serious injuries?</p>
              <div className="space-y-2">
                {SERIOUS_INJURIES.map(({ value, label }) => (
                  <label key={value} className="flex items-start gap-2.5 cursor-pointer m-0 font-normal">
                    <input type="checkbox" className="mt-0.5 flex-shrink-0" style={{ width: '1rem', height: '1rem' }}
                      checked={form.specified_serious_injuries.includes(value)}
                      onChange={() => toggleSSI(value)} />
                    <span className="text-sm" style={{ color: 'var(--text)' }}>{label}</span>
                  </label>
                ))}
                <label className="flex items-center gap-2.5 cursor-pointer m-0 font-normal pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                  <input type="checkbox" className="flex-shrink-0" style={{ width: '1rem', height: '1rem' }}
                    checked={form.specified_serious_injuries.includes('none')}
                    onChange={() => toggleSSI('none')} />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>None of the above</span>
                </label>
              </div>
            </div>

            {/* Q4: Dangerous incident */}
            <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <div className="flex items-start gap-2">
                <p className="text-sm font-semibold flex-1" style={{ color: 'var(--text)' }}>4. Was this a dangerous incident even if no one was injured?</p>
              </div>
              <details className="text-xs" style={{ color: 'var(--text-muted)' }}>
                <summary className="cursor-pointer flex items-center gap-1 font-medium" style={{ color: 'var(--accent)', listStyle: 'none' }}>
                  <Info size={12} /> What counts as a dangerous incident?
                </summary>
                <ul className="mt-2 space-y-1 pl-3">
                  {DANGEROUS_INCIDENT_EXAMPLES.map(ex => <li key={ex}>• {ex}</li>)}
                </ul>
              </details>
              <div className="flex gap-3">
                {[true, false].map(v => (
                  <button key={String(v)} type="button"
                    onClick={() => setForm(f => ({ ...f, dangerous_incident: v }))}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-all"
                    style={{
                      background: form.dangerous_incident === v ? (v ? '#FFEBE6' : 'var(--accent-light)') : 'var(--surface)',
                      color: form.dangerous_incident === v ? (v ? '#D93025' : 'var(--accent)') : 'var(--text-muted)',
                      border: form.dangerous_incident === v ? `2px solid ${v ? '#D93025' : 'var(--accent)'}` : '1px solid var(--border)',
                    }}>
                    {v ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
            </div>

            {/* Live classification result */}
            {isActuallyNotifiable ? (
              <div className="p-4 rounded-xl flex items-start gap-3" style={{ background: '#FFEBE6', border: '2px solid #D93025' }}>
                <AlertCircle size={18} style={{ color: '#D93025', flexShrink: 0, marginTop: '0.1rem' }} />
                <div>
                  <p className="font-bold text-sm" style={{ color: '#D93025' }}>Notifiable Incident — SafeWork notification required</p>
                  <p className="text-xs mt-0.5" style={{ color: '#8A2010' }}>
                    This incident meets the WHS Act s35/s36 definition of a notifiable incident. After submitting, you will be guided through scene preservation and the SafeWork phone notification (within 1 hour) and written notice (within 48 hours).
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-xl flex items-start gap-2.5" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <CheckCircle size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: '0.1rem' }} />
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Based on your answers, this does not appear to be a notifiable incident. It will be managed through the standard internal incident process. A safety manager can reclassify after submission if needed.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── 5. Person Involved ── shown for any injury event */}
        {isInjuryEvent && (
          <div className="card space-y-4">
            <div>
              <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Person Involved</h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Required for the WHS Register of Injuries and notifiable incident reporting</p>
            </div>

            {/* Person mode selector */}
            <div className="grid grid-cols-3 gap-2">
              {([['me', 'Me (self-reported)'], ['search', 'Search team member'], ['visitor', 'Visitor / Public']] as const).map(([mode, label]) => (
                <button key={mode} type="button"
                  onClick={() => mode === 'me' ? selectMe() : mode === 'visitor' ? selectVisitor() : selectSearch()}
                  className="text-sm rounded-lg border py-2 px-3 font-medium transition-all"
                  style={{
                    background: personMode === mode ? 'var(--accent)' : 'var(--bg)',
                    color: personMode === mode ? '#fff' : 'var(--text)',
                    border: personMode === mode ? '2px solid var(--accent)' : '1px solid var(--border)',
                  }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Workforce search */}
            {personMode === 'search' && (
              <div className="relative">
                <label>Search by name</label>
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Start typing a name…" autoFocus />
                {(searching || searchResults.length > 0) && (
                  <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-xl shadow-lg overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    {searching && <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-muted)' }}>Searching…</div>}
                    {searchResults.map((m: any) => (
                      <button key={m.id} type="button" onClick={() => pickWorkforce(m)}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
                          {m.first_name?.[0]}{m.last_name?.[0]}
                        </div>
                        <div>
                          <div className="font-medium" style={{ color: 'var(--text)' }}>{m.first_name} {m.last_name}</div>
                          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{m.job_title || ''} · {m.is_contractor ? 'Contractor' : 'Own staff'}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Person fields — shown after any mode selected */}
            {personMode !== null && (
              <div className="space-y-3">
                {personMode !== 'me' && personMode !== 'search' ? null : personMode === 'search' && !form.person_name ? null : null}
                {(personMode === 'visitor' || form.person_name !== undefined) && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label>Full Name {hasInjury && '*'}</label>
                      <input name="person_name" value={form.person_name} onChange={handleChange} placeholder="As per ID" />
                    </div>
                    <div>
                      <label>Relationship to Business</label>
                      <select name="person_relationship" value={form.person_relationship} onChange={handleChange}>
                        <option value="">— select —</option>
                        {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label>Date of Birth {hasInjury && <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>(required for register)</span>}</label>
                      <input type="date" name="person_dob" value={form.person_dob} onChange={handleChange} />
                    </div>
                    <div>
                      <label>Contact Number</label>
                      <input type="tel" name="person_contact" value={form.person_contact} onChange={handleChange} placeholder="Mobile or landline" />
                    </div>
                    <div className="col-span-2">
                      <label>Address {hasInjury && <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>(required for register)</span>}</label>
                      <input name="person_address" value={form.person_address} onChange={handleChange} placeholder="Street, suburb, state, postcode" />
                    </div>
                  </div>
                )}
                {personMode === 'me' && (
                  <p className="text-xs p-3 rounded-lg" style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
                    ✓ Pre-filled from your profile. Add your date of birth and address below if not shown — this speeds up future incident reports.
                  </p>
                )}
                {personMode === 'search' && form.person_name && (
                  <p className="text-xs p-3 rounded-lg" style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
                    ✓ Pre-filled from workforce register. Verify details are current before submitting.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── 6. Treatment & Hospital ── shown when MTI or LTI */}
        {(form.medical_treatment || form.lost_time) && (
          <div className="card space-y-4">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Treatment & Hospital</h2>
            <div>
              <label>Treatment Received</label>
              <textarea name="treatment_received" value={form.treatment_received} onChange={handleChange} rows={2} placeholder="Describe treatment provided or received…" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer m-0 font-normal">
                <input type="checkbox" name="taken_to_hospital" checked={form.taken_to_hospital} onChange={handleChange} className="w-4 h-4" style={{ width: '1rem' }} />
                <span className="text-sm" style={{ color: 'var(--text)' }}>Taken to hospital</span>
              </label>
            </div>
            {form.taken_to_hospital && (
              <div className="grid grid-cols-2 gap-4">
                <div><label>Hospital Name</label><input name="hospital_name" value={form.hospital_name} onChange={handleChange} placeholder="e.g. Westmead Hospital" /></div>
                <div><label>Treating Practitioner</label><input name="treating_practitioner" value={form.treating_practitioner} onChange={handleChange} placeholder="Dr / Nurse name" /></div>
              </div>
            )}
          </div>
        )}

        {/* ── 7. Immediate Site Actions ── always for incident category */}
        {isInjuryEvent && (
          <div className="card space-y-4">
            <div>
              <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Immediate Site Actions</h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>These are the first things the regulator will ask about. Separate from longer-term corrective actions.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: 'first_aid_administered', label: 'First aid administered' },
                { name: 'area_secured', label: 'Area secured / isolated' },
                { name: 'supervisor_notified', label: 'Supervisor notified' },
              ].map(({ name, label }) => (
                <label key={name} className="flex items-center gap-2 cursor-pointer m-0 font-normal">
                  <input type="checkbox" name={name} checked={(form as any)[name]} onChange={handleChange} className="w-4 h-4" style={{ width: '1rem' }} />
                  <span className="text-sm" style={{ color: 'var(--text)' }}>{label}</span>
                </label>
              ))}
            </div>
            <div>
              <label>Immediate actions to make site safe <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>(for SafeWork phone call — not corrective actions)</span></label>
              <textarea name="immediate_actions" value={form.immediate_actions} onChange={handleChange} rows={3} placeholder="e.g. Area cordoned, power isolated, injured party transported to hospital, SWMS reviewed with crew…" />
            </div>
          </div>
        )}

        {/* ── 8. Witnesses ── */}
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Witnesses</h2>
            <button type="button" onClick={addWitness} className="btn btn-secondary text-sm flex items-center gap-1.5" style={{ padding: '0.375rem 0.75rem' }}>
              <Plus size={14} /> Add Witness
            </button>
          </div>
          {witnesses.length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No witnesses added. Tap Add Witness if there were any present.</p>}
          {witnesses.map((w, i) => (
            <div key={i} className="p-3 rounded-xl space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Witness {i + 1}</span>
                <button type="button" onClick={() => removeWitness(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label>Name *</label><input value={w.name} onChange={e => updateWitness(i, 'name', e.target.value)} placeholder="Full name" /></div>
                <div><label>Contact Number</label><input value={w.contact} onChange={e => updateWitness(i, 'contact', e.target.value)} placeholder="Mobile or email" /></div>
              </div>
              <div><label>Statement <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
                <textarea value={w.statement} onChange={e => updateWitness(i, 'statement', e.target.value)} rows={2} placeholder="Brief account of what they witnessed…" />
              </div>
            </div>
          ))}
        </div>

        {submitError && (
          <div className="p-4 rounded-xl flex items-start gap-3" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" /><span className="text-sm">{submitError}</span>
          </div>
        )}

        <div className="flex gap-3">
          <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Submitting…' : 'Submit & Analyse'}</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/incidents')}>Cancel</button>
        </div>
      </form>
    </div>
  )
}
